import {
  useAccountId,
  useAuthSignature,
  useWallet
} from "@buidlerlabs/hashgraph-react-wallets";
import AuthPage from "../../views/AuthPage";
import { useCallback, useEffect, useState } from "react";
import { URLs } from "../../utils/constants";
import { FullPageLoader } from "../../components/reusable/FullPageLoader";
import { showNotification } from "@mantine/notifications";
import { IconX } from "@tabler/icons";

export const UserSessionGuard = ({ children }) => {
  const { isConnected } = useWallet();
  const { data: accountId } = useAccountId();
  const { signAuth } = useAuthSignature();
  const [state, setState] = useState({
    loading: isConnected,
    isAuthenticating: false,
    canActivate: false
  });

  const handleAuthenticate = useCallback(async () => {
    try {
      setState((prevState) => ({
        ...prevState,
        isAuthenticating: true
      }));

      const challengeResponse = await fetch(`${URLs.API}/auth/token/challenge`);
      const jTChallenge = await challengeResponse.json();

      const walletSignature = await signAuth(
        JSON.stringify({
          serverSignature: jTChallenge.server.signature,
          originalPayload: jTChallenge.payload
        })
      );

      const loginResponse = await fetch(`${URLs.API}/auth/token`, {
        method: "POST",
        credentials: "include",
        redirect: "follow",
        body: JSON.stringify({
          payload: jTChallenge.payload,
          signatures: {
            server: jTChallenge.server.signature,
            wallet: {
              publicKey: walletSignature.publicKey.toStringRaw(),
              value: Buffer.from(walletSignature.signature).toString("hex"),
              hedera: {
                accountId: walletSignature.accountId.toString()
              }
            }
          }
        })
      });

      if (!loginResponse.ok) {
        const message = await loginResponse.text();
        throw new Error(message);
      }

      setState((prevState) => ({
        ...prevState,
        canActivate: true
      }));
    } catch (error) {
      console.error(error);

      showNotification({
        icon: <IconX />,
        autoClose: 4000,
        color: "red",
        title: `Failed to authenticate`,
        message:
          error.message ||
          "Something went wrong. Check console for more details",
        loading: false
      });
    } finally {
      setState((prevState) => ({
        ...prevState,
        loading: false,
        isAuthenticating: false
      }));
    }
  }, [signAuth]);

  const handleCheckSession = useCallback(async () => {
    try {
      const response = await fetch(`${URLs.API}/auth/ping`, {
        credentials: "include"
      });

      if (!response?.ok) {
        const { message } = await response.json();
        throw new Error(message);
      }

      setState((prevState) => ({
        ...prevState,
        canActivate: true,
        loading: false
      }));
    } catch (error) {
      console.error(error);

      if (error.message.includes("Unauthorized")) {
        handleAuthenticate();
      }
    }
  }, [handleAuthenticate]);

  useEffect(() => {
    if (isConnected && accountId) {
      handleCheckSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, accountId]);

  if (state.loading) {
    return <FullPageLoader label="Performing authentication..." />;
  }

  return isConnected && state.canActivate ? (
    children
  ) : (
    <AuthPage
      isAuthenticating={state.isAuthenticating}
      onAuthenticate={handleAuthenticate}
    />
  );
};
